/** The evidence stack of one proposal, shared by the phone detail and the desktop pane. */

import type { Proposal, Snapshot } from '../api/types.js';
import { AGENT_NAMES, AGENT_SHAPES } from '../app/selectors.js';
import {
  CheckStrip,
  CouponTerms,
  DiffBlock,
  DraftBlock,
  GenericDiff,
  GenericFields,
  GenericPreview,
  PolicyTrack,
  QuotedInput,
  ReasoningLines,
  RunLink,
  SourceChips,
  VerifierNotice,
} from '../components/evidence.js';
import { AgentGlyph, Icon } from '../components/icons.js';
import { TierBadge } from '../components/primitives.js';
import { SourceLine } from '../components/source.js';
import { formatClock } from '../lib/format.js';

export function ProposalBody({
  proposal,
  snapshot,
  desktop = false,
  titleId,
}: {
  proposal: Proposal;
  snapshot: Snapshot;
  desktop?: boolean;
  titleId: string;
}) {
  const run = snapshot.runs.find((item) => item.id === proposal.runId) ?? null;
  if (proposal.kind === 'coupon') {
    return (
      <div className="pbody">
        <div className="t4-head">
          <TierBadge tier="T4" topic={proposal.topic} variant="detail" />
          <span className="t4-head__note">
            <Icon name="lock" size={12} stroke={2} />
            Karar senin · hızlı onay kapalı
          </span>
        </div>
        <h1 id={titleId} className="pbody__title">
          {proposal.title}
        </h1>
        <div className="pbody__by">
          <AgentGlyph shape={AGENT_SHAPES[proposal.agent]} />
          {AGENT_NAMES[proposal.agent]} · <span className="mono">{formatClock(proposal.draftedAt)}</span>
        </div>
        <SourceLine source={proposal.source} />
        <VerifierNotice proposal={proposal} />
        <CouponTerms proposal={proposal} />
        <CheckStrip checks={proposal.checks} />
        <PolicyTrack value={proposal.terms.discountPct} cap={proposal.policy.capPct} />
        <p className="pbody__impact">{proposal.impact}</p>
        <ReasoningLines items={proposal.reasoning} title="Neden bu öneri" />
        {run ? <RunLink run={run} /> : null}
      </div>
    );
  }
  return (
    <div className="pbody">
      <div className="pbody__tier-row">
        <TierBadge tier={proposal.tier} topic={proposal.topic} variant={desktop ? 'full' : 'detail'} />
        <h1 id={titleId} className="pbody__kind ellipsis">
          {proposal.kindLabel}
        </h1>
        <span className="pbody__by">
          <AgentGlyph shape={AGENT_SHAPES[proposal.agent]} />
          {AGENT_NAMES[proposal.agent]} · <span className="mono">{formatClock(proposal.draftedAt)}</span>
        </span>
      </div>
      <SourceLine source={proposal.source} />
      <VerifierNotice proposal={proposal} />
      {proposal.tier !== 'T3' ? (
        <p className="t4-head__note">
          <Icon name="lock" size={12} stroke={2} />
          Karar senin · hızlı onay kapalı
        </p>
      ) : null}
      {proposal.kind === 'customer_reply' ? <QuotedInput proposal={proposal} /> : null}
      {proposal.kind === 'generic' ? <GenericFields proposal={proposal} /> : null}
      <CheckStrip
        checks={proposal.checks}
        fact={
          desktop
            ? {
                key: 'Hazırlayan',
                value: `${AGENT_NAMES[proposal.agent]} ·`,
                valueMono: formatClock(proposal.draftedAt),
                result: 'fact',
              }
            : null
        }
      />
      {proposal.kind === 'customer_reply' ? <DraftBlock proposal={proposal} flat={desktop} /> : null}
      {proposal.kind === 'listing_title' ? <DiffBlock proposal={proposal} /> : null}
      {proposal.kind === 'generic' ? (
        <>
          <GenericPreview proposal={proposal} />
          <GenericDiff proposal={proposal} />
        </>
      ) : null}
      <ReasoningLines
        items={proposal.reasoning}
        title={proposal.kind === 'customer_reply' ? 'Neden bu cevap' : 'Neden bu değişiklik'}
      />
      <SourceChips sources={proposal.sources} row={desktop} />
      {run ? <RunLink run={run} /> : null}
    </div>
  );
}
